import { useQuery } from '@apollo/client';

import { PlansVariant } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';

import {
  OnboardingPackagesDocument,
  OnboardingPackagesQuery,
} from '../../graphql/queries/onboardingPackages.gql';
import {
  OnboardingPlansDocument,
  OnboardingPlansQuery,
} from '../../graphql/queries/onboardingPlans.gql';
import {
  OnboardingPackageItem,
  OnboardingPlanItem,
  PricingPlansModuleData,
} from '../../machine/model';
import PlansDesktop from './components/PlansDesktop';
import PlansMobile from './components/PlansMobile';
import AskVariant from './components/PlansMobile/components/AskVariant';
import getOnboardingPlanDisplay from './lib/getOnboardingPlanDisplay';
import { OnboardingPlanDisplay } from './types';

interface PlanSelectionProps {
  moduleData: PricingPlansModuleData;
  referrer?: string;
  onShowDetails: () => void;
  onChoosePlan: (plan: OnboardingPlanItem) => void;
  onChoosePackage: (selectedPackage: OnboardingPackageItem) => void;
  shouldShowPackagePriceWithVAT: boolean;
  appliedCoupon: string | null;
  variant: PlansVariant | null;
  setVariant: (variant: PlansVariant | null) => void;
  isMobile: boolean;
}

/**
 * A page where the user can see plans main features and select one.
 */
const PlanSelection = asBentoPage<PlanSelectionProps>(
  ({
    appliedCoupon,
    isMobile,
    moduleData,
    onChoosePackage,
    onChoosePlan,
    onPrev,
    referrer,
    setVariant,
    shouldShowPackagePriceWithVAT,
    variant,
  }) => {
    const { packageType, viewer } = moduleData;
    const {
      companyProfileId,
      metadata: companyMetadata,
      shouldSeeProAndBusinessPlans,
    } = viewer.company;

    const shouldShowVariantSelector =
      shouldSeeProAndBusinessPlans && packageType === null;

    const { data } = useQuery<OnboardingPackagesQuery | OnboardingPlansQuery>(
      packageType ? OnboardingPackagesDocument : OnboardingPlansDocument,
      {
        // We want the variant not to be null to query plans, otherwise we would get 4 results
        skip: !packageType && !variant,
        variables: {
          companyProfileId,
          input: packageType
            ? {
                coupon: appliedCoupon,
                type: packageType,
              }
            : { variant },
        },
      },
    );

    const company = data?.viewer.company;

    const plans =
      company && 'onboardingPlans' in company
        ? company.onboardingPlans
        : company?.onboardingPackages.map(({ plan }) => plan) ?? [];

    const packages =
      company && 'onboardingPackages' in company
        ? company.onboardingPackages
        : null;

    const paymentCardLimits = {
      basicDebitCard: moduleData.basicDebitCardLimits,
      businessWorldDebitCard: moduleData.businessWorldDebitCardLimits,
    };

    const onboardingPlanDisplays = (packages || plans).map((item) =>
      getOnboardingPlanDisplay(
        item as OnboardingPlanItem | OnboardingPackageItem,
        shouldShowPackagePriceWithVAT,
        paymentCardLimits,
      ),
    );

    const shouldAskVariant = isMobile && !variant && shouldShowVariantSelector;

    if (shouldAskVariant) {
      return (
        <AskVariant
          onChooseVariant={(selectedVariant) => {
            logEvent({
              name: 'Price Plan Variant Selected',
              properties: {
                variant: selectedVariant.toLowerCase(),
              },
            });
            setVariant(selectedVariant);
          }}
          onPrev={onPrev}
        />
      );
    }

    const onChoosePlanDisplay = (planDisplay: OnboardingPlanDisplay) => {
      logEvent({
        name: 'Price Plan Selected',
        properties: {
          isMobile,
          plan: planDisplay.providerPlanId,
          variant: variant?.toLowerCase(),
        },
      });

      if (planDisplay.providerPackageId) {
        const selectedPackage =
          packages &&
          (packages?.find(
            ({ providerPackageId }) =>
              providerPackageId === planDisplay.providerPackageId,
          ) as OnboardingPackageItem | undefined);

        if (selectedPackage) {
          return onChoosePackage(selectedPackage);
        } else {
          throw new Error(`Package not found: ${JSON.stringify(planDisplay)}`);
        }
      }

      if (!(company && 'onboardingPlans' in company)) {
        throw new Error('Impossible to find plan');
      }

      const plan = company.onboardingPlans.find(
        ({ providerPlanId }) => providerPlanId === planDisplay.providerPlanId,
      );

      if (plan) {
        return onChoosePlan(plan);
      } else {
        throw new Error(
          `Plan or package not found: ${JSON.stringify(planDisplay)}`,
        );
      }
    };

    if (isMobile) {
      return onboardingPlanDisplays.length ? (
        <PlansMobile
          onChoosePlanDisplay={onChoosePlanDisplay}
          onPrev={onPrev}
          planDisplays={onboardingPlanDisplays}
          referrer={referrer}
          selectedPlanId={companyMetadata.selectedPlanId}
          shouldShowPackagePriceWithVAT={shouldShowPackagePriceWithVAT}
          variant={variant}
        />
      ) : (
        <Loader />
      );
    } else {
      return (
        <PlansDesktop
          onboardingPlanDisplays={onboardingPlanDisplays}
          onChoosePlanDisplay={onChoosePlanDisplay}
          onClose={onPrev}
          referrer={referrer}
          selectedPlanId={companyMetadata.selectedPlanId}
          setVariant={setVariant}
          shouldShowPackagePriceWithVAT={shouldShowPackagePriceWithVAT}
          shouldShowVariantSelector={shouldShowVariantSelector}
          variant={variant}
        />
      );
    }
  },
);

export default PlanSelection;
